import React, { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import { API_URL } from "../config/api";
import { useNavigate } from "react-router-dom";
import LoadingIndicator from "../Components/LoadingIndicator";

const PaymentReport = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [appliedSearch, setAppliedSearch] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [limit, setLimit] = useState(25);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const navigate = useNavigate();
  const [sortBy, setSortBy] = useState("createdAt");
  const [sortOrder, setSortOrder] = useState("desc");

  useEffect(() => {
    fetchPayments();
  }, [currentPage, appliedSearch, limit, sortBy, sortOrder]);

  const fetchPayments = async () => {
    setIsLoading(true);
    try {
      const payload = {
        action: "listPaymentHistory",
        page: String(currentPage),
        limit: String(limit),
        search: appliedSearch || undefined,
        sortBy,
        sortOrder,
      };

      const response = await axios.post(API_URL, payload, {
        headers: { "Content-Type": "application/json" },
      });

      const data = response.data;

      setRows(data.data || []);
      setTotalPages(data.pagination?.totalPages || 1);
      setTotalCount(data.pagination?.total || 0);
    } catch (error) {
      const message =
        error.response?.data?.message || "Failed to load payments";
      toast.error(message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setAppliedSearch(searchTerm);
    setCurrentPage(1);
  };

  const handleClearSearch = () => {
    setSearchTerm("");
    setAppliedSearch("");
    setCurrentPage(1);
  };

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) setCurrentPage(newPage);
  };

  const formatDateTime = (value) => {
    if (!value) return "-";
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return value;
    return date
      .toLocaleString("en-IN", {
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
        hour12: true,
      })
      .replace(",", " ");
  };

  const handleSort = (column) => {
    if (sortBy !== column) {
      setSortBy(column);
      setSortOrder("desc");
    } else if (sortOrder === "desc") {
      setSortOrder("asc");
    } else {
      setSortBy("createdAt");
      setSortOrder("desc");
    }

    setCurrentPage(1);
  };

  const renderSortIcon = (column) => {
    if (sortBy !== column) {
      return (
        <i
          className="fa-solid fa-sort"
          style={{
            fontSize: "10px",
            opacity: 0.35,
            marginLeft: "5px",
          }}
        ></i>
      );
    }

    return sortOrder === "asc" ? (
      <i
        className="fa-solid fa-sort-up"
        style={{
          fontSize: "10px",
          marginLeft: "8px",
          verticalAlign: "middle",
        }}
      ></i>
    ) : (
      <i
        className="fa-solid fa-sort-down"
        style={{
          fontSize: "10px",
          marginLeft: "8px",
          verticalAlign: "middle",
        }}
      ></i>
    );
  };

  const handleLimitChange = (e) => {
    setLimit(Number(e.target.value));
    setCurrentPage(1);
  };

  const renderPagination = () => {
    const pages = [];
    const maxVisiblePages = 5;

    let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));

    let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);

    if (endPage - startPage + 1 < maxVisiblePages) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1);
    }

    for (let i = startPage; i <= endPage; i++) {
      pages.push(i);
    }

    return (
      <div className="pagination-wrapper">
        <div className="d-flex align-items-center">
          <span className="mr-2">Show</span>

          <select
            className="form-control"
            style={{ width: "80px" }}
            value={limit}
            onChange={handleLimitChange}
          >
            <option value={25}>25</option>
            <option value={50}>50</option>
            <option value={100}>100</option>
          </select>

          <span className="ml-2 mr-3">Entries</span>

          <span className="pagination-info">
            Showing {rows.length > 0 ? (currentPage - 1) * limit + 1 : 0} to{" "}
            {Math.min(currentPage * limit, totalCount)} of {totalCount} entries
          </span>
        </div>

        <div className="pagination-controls">
          <button
            className="pagination-btn"
            disabled={currentPage === 1}
            onClick={() => handlePageChange(1)}
          >
            <i className="fas fa-angle-double-left"></i>
          </button>

          <button
            className="pagination-btn"
            disabled={currentPage === 1}
            onClick={() => handlePageChange(currentPage - 1)}
          >
            <i className="fas fa-angle-left"></i>
          </button>

          {pages.map((page) => (
            <button
              key={page}
              className={`pagination-btn ${
                currentPage === page ? "active" : ""
              }`}
              onClick={() => handlePageChange(page)}
            >
              {page}
            </button>
          ))}

          <button
            className="pagination-btn"
            disabled={currentPage === totalPages}
            onClick={() => handlePageChange(currentPage + 1)}
          >
            <i className="fas fa-angle-right"></i>
          </button>

          <button
            className="pagination-btn"
            disabled={currentPage === totalPages}
            onClick={() => handlePageChange(totalPages)}
          >
            <i className="fas fa-angle-double-right"></i>
          </button>
        </div>
      </div>
    );
  };

  return (
    <div className="employee-form">
      <div className="content-header subscription-header">
        <div className="row align-items-center">
          <div className="col-sm-6">
            <h1>Payment Report</h1>
            <p>View all payment entries.</p>
          </div>
        </div>
      </div>

      <div className="card employee-content-card">
        <div className="card-body">
          <div className="row mb-3 align-items-end">
            <div className="col-xl-5 col-lg-6 col-md-8 col-sm-12 mb-2">
              <form onSubmit={handleSearch}>
                <label className="font-weight-bold mb-1">Search</label>

                <div className="input-group">
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Search ..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />

                  <div className="input-group-append">
                    <button
                      type="submit"
                      className="btn btn-primary"
                      disabled={isLoading}
                    >
                      <i className="fas fa-search"></i>
                    </button>

                    {searchTerm && (
                      <button
                        type="button"
                        className="btn btn-secondary"
                        onClick={handleClearSearch}
                        disabled={isLoading}
                      >
                        <i className="fas fa-times"></i>
                      </button>
                    )}
                  </div>
                </div>
              </form>
            </div>
          </div>
          {isLoading ? (
            <div className="employee-loading">
              <LoadingIndicator size={120} />
            </div>
          ) : rows.length === 0 ? (
            <div className="employee-empty">No payment data found.</div>
          ) : (
            <>
              <div className="employee-table-wrapper">
                <table className="table table-borderless employee-table subscription-table">
                  <thead style={{ fontSize: "12px", whiteSpace: "nowrap" }}>
                    <tr>
                      <th>Sl No.</th>

                      <th
                        onClick={() => handleSort("name")}
                        style={{ cursor: "pointer", userSelect: "none" }}
                      >
                        Name {renderSortIcon("name")}
                      </th>

                      <th
                        onClick={() => handleSort("email")}
                        style={{ cursor: "pointer", userSelect: "none" }}
                      >
                        Email {renderSortIcon("email")}
                      </th>

                      <th
                        onClick={() => handleSort("subscriptionPlan")}
                        style={{ cursor: "pointer", userSelect: "none" }}
                      >
                        Subscription Plan {renderSortIcon("subscriptionPlan")}
                      </th>

                      <th
                        onClick={() => handleSort("method")}
                        style={{ cursor: "pointer", userSelect: "none" }}
                      >
                        Method {renderSortIcon("method")}
                      </th>

                      <th
                        onClick={() => handleSort("status")}
                        style={{ cursor: "pointer", userSelect: "none" }}
                      >
                        Status {renderSortIcon("status")}
                      </th>

                      <th>Action</th>
                    </tr>
                  </thead>

                  <tbody>
                    {rows.map((row, idx) => (
                      <tr key={row._id || idx}>
                        <td>{(currentPage - 1) * limit + idx + 1}</td>
                        <td>{row.name || "-"}</td>
                        <td>{row.email || "-"}</td>
                        <td>{row.subscriptionPlan || "-"}</td>
                        <td>{row.method || "-"}</td>
                        <td>{row.status || "-"}</td>
                        <td>
                          <div className="employee-actions">
                            <button
                              className="action-icon-btn view"
                              type="button"
                              title="View"
                              onClick={() =>
                                navigate(`/payment-view/${row._id}`)
                              }
                            >
                              <i className="fa-solid fa-eye"></i>
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {renderPagination()}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default PaymentReport;
